import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { getUserPrivileges, getUserRole, isAuthenticated } from '../../../../utils/auth';
import { PRODUCT_MASTER_PRIVILEGES } from '../../../../utils/productMasterPrivileges';
import Sidebar from '../../../../components/Sidebar/sidebar';
import Header from '../../../../components/Header';
import Pagination from '../../../../components/Pagination/Pagination';
import './CategoryMaster.css';
import api from '../../../../utils/api';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import {
    ArchiveRestore,
    Ban,
    ChevronDown,
    Download,
    Edit,
    FolderTree,
    Layers3,
    Plus,
    RefreshCw,
    Search,
    ShieldCheck,
    Sparkles,
    Tags,
    X
} from 'lucide-react';
import { API_BASE_URL } from '../../../../utils/apiBase';
import { toast } from '../../../../components/notifications/notificationService';

const emptyForm = {
    categoryName: '',
    hasRider: false,
    hasScheme: false
};

const CategoryMaster = () => {
    const privileges = getUserPrivileges();
    const isAdmin = getUserRole() === 'admin';
    const hasPrivilege = (key) => isAdmin || privileges.includes(key);
    const categoryPrivileges = PRODUCT_MASTER_PRIVILEGES.category;
    const [collapsed, setCollapsed] = useState(false);
    const [authenticated, setAuthenticated] = useState(false);
    const [checkingAuth, setCheckingAuth] = useState(true);
    const [showPopup, setShowPopup] = useState(false);
    const [formData, setFormData] = useState(emptyForm);
    const [categories, setCategories] = useState([]);
    const [editMode, setEditMode] = useState(false);
    const [currentCategoryId, setCurrentCategoryId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [categoryToDelete, setCategoryToDelete] = useState(null);
    const [showInactivePopup, setShowInactivePopup] = useState(false);
    const [inactiveList, setInactiveList] = useState([]);
    const [activeSearchText, setActiveSearchText] = useState('');
    const [activeRiderFilter, setActiveRiderFilter] = useState('');
    const [activeSchemeFilter, setActiveSchemeFilter] = useState('');
    const [inactiveSearchText, setInactiveSearchText] = useState('');
    const [sortField, setSortField] = useState('');
    const [sortOrder, setSortOrder] = useState('asc');
    const [activePage, setActivePage] = useState(1);
    const [activeItemsPerPage, setActiveItemsPerPage] = useState(10);
    const [inactivePage, setInactivePage] = useState(1);
    const [inactiveItemsPerPage, setInactiveItemsPerPage] = useState(10);
    const [showExportMenu, setShowExportMenu] = useState(false);

    useEffect(() => {
        const auth = isAuthenticated();
        setAuthenticated(auth);
        setCheckingAuth(false);
    }, []);

    const showNotification = useCallback((message, type) => {
        (toast[type] || toast.info)(message);
    }, []);

    const fetchCategories = useCallback(async () => {
        setLoading(true);
        try {
            const response = await api.get(`${API_BASE_URL}/api/categories`);
            setCategories(response.data || []);
        } catch (error) {
            showNotification('Error fetching categories', 'error');
        } finally {
            setLoading(false);
        }
    }, [showNotification]);

    useEffect(() => {
        if (!authenticated) return;
        fetchCategories();
    }, [authenticated, fetchCategories]);

    const filteredActive = useMemo(() => {
        const text = activeSearchText.trim().toLowerCase();
        return categories.filter((category) => {
            const matchesText = !text || category.categoryName?.toLowerCase().includes(text);
            const matchesRider = !activeRiderFilter || String(Boolean(category.hasRider)) === activeRiderFilter;
            const matchesScheme = !activeSchemeFilter || String(Boolean(category.hasScheme)) === activeSchemeFilter;

            return matchesText && matchesRider && matchesScheme;
        });
    }, [categories, activeSearchText, activeRiderFilter, activeSchemeFilter]);

    const filteredInactive = useMemo(() => {
        const text = inactiveSearchText.trim().toLowerCase();
        return inactiveList
            .filter((item) => !text || item.categoryName?.toLowerCase().includes(text))
            .sort((a, b) => {
                if (!sortField) return 0;

                const valA = sortField === 'hasRider' || sortField === 'hasScheme'
                    ? Number(Boolean(a[sortField]))
                    : (a[sortField] || '').toString();
                const valB = sortField === 'hasRider' || sortField === 'hasScheme'
                    ? Number(Boolean(b[sortField]))
                    : (b[sortField] || '').toString();

                if (typeof valA === 'number' && typeof valB === 'number') {
                    return sortOrder === 'asc' ? valA - valB : valB - valA;
                }

                return sortOrder === 'asc'
                    ? String(valA).localeCompare(String(valB))
                    : String(valB).localeCompare(String(valA));
            });
    }, [inactiveList, inactiveSearchText, sortField, sortOrder]);

    const activeLast = activePage * activeItemsPerPage;
    const activeFirst = activeLast - activeItemsPerPage;
    const activeCategories = filteredActive.slice(activeFirst, activeLast);

    const inactiveLast = inactivePage * inactiveItemsPerPage;
    const inactiveFirst = inactiveLast - inactiveItemsPerPage;
    const inactiveCategories = filteredInactive.slice(inactiveFirst, inactiveLast);

    useEffect(() => {
        setActivePage(1);
    }, [activeSearchText, activeRiderFilter, activeSchemeFilter]);

    useEffect(() => {
        setInactivePage(1);
    }, [inactiveSearchText, sortField, sortOrder]);

    const summaryCards = [
        { label: 'Active Categories', value: categories.length, note: 'Available in product setup', icon: Tags, tone: 'is-blue' },
        { label: 'Rider Enabled', value: categories.filter((item) => item.hasRider).length, note: 'Supports rider mapping', icon: ShieldCheck, tone: 'is-green' },
        { label: 'Scheme Enabled', value: categories.filter((item) => item.hasScheme).length, note: 'Supports scheme setup', icon: Sparkles, tone: 'is-amber' },
        { label: 'Simple Categories', value: categories.filter((item) => !item.hasRider && !item.hasScheme).length, note: 'No rider or scheme flags', icon: Layers3, tone: 'is-teal' },
        { label: 'Inactive Categories', value: inactiveList.length, note: 'Loaded from inactive list', icon: ArchiveRestore, tone: 'is-indigo' }
    ];

    const resetForm = () => {
        setShowPopup(false);
        setFormData(emptyForm);
        setEditMode(false);
        setCurrentCategoryId(null);
    };

    const openAddPopup = () => {
        resetForm();
        setShowPopup(true);
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();

        if (!formData.categoryName.trim()) {
            showNotification('Category Name is required', 'error');
            return;
        }

        try {
            if (editMode) {
                await api.put(`${API_BASE_URL}/api/categories/${currentCategoryId}`, formData);
                setCategories((prev) =>
                    prev.map((cat) => (cat._id === currentCategoryId ? { ...cat, ...formData } : cat))
                );
                showNotification('Category updated successfully', 'success');
            } else {
                const res = await api.post(`${API_BASE_URL}/api/categories`, formData);
                setCategories((prev) => [...prev, res.data.category]);
                showNotification('Category added successfully', 'success');
            }

            resetForm();
        } catch (err) {
            showNotification('Error saving category', 'error');
        }
    };

    const handleEditCategory = (category) => {
        setFormData({
            categoryName: category.categoryName || '',
            hasRider: Boolean(category.hasRider),
            hasScheme: Boolean(category.hasScheme)
        });
        setCurrentCategoryId(category._id);
        setEditMode(true);
        setShowPopup(true);
    };

    const openInactivePopup = async () => {
        try {
            const res = await api.get(`${API_BASE_URL}/api/categories/inactive/list`);
            setInactiveList(res.data || []);
            setInactiveSearchText('');
            setSortField('');
            setSortOrder('asc');
            setInactivePage(1);
            setShowInactivePopup(true);
        } catch (err) {
            showNotification('Error fetching inactive categories', 'error');
        }
    };

    const confirmDelete = async () => {
        try {
            await api.put(`${API_BASE_URL}/api/categories/deactivate/${categoryToDelete}`);
            setCategories((prev) => prev.filter((cat) => cat._id !== categoryToDelete));
            showNotification('Category deactivated successfully', 'success');
        } catch (err) {
            showNotification(err.response?.data?.message || 'Error deactivating category', 'error');
        } finally {
            setShowDeleteConfirm(false);
            setCategoryToDelete(null);
        }
    };

    const activateCategory = async (id) => {
        try {
            await api.put(`${API_BASE_URL}/api/categories/activate/${id}`);
            const activated = inactiveList.find((cat) => cat._id === id);
            setInactiveList((prev) => prev.filter((cat) => cat._id !== id));
            if (activated) setCategories((prev) => [...prev, activated]);
            showNotification('Category activated successfully', 'success');
        } catch (err) {
            showNotification('Error activating category', 'error');
        }
    };

    const restoreAll = async () => {
        try {
            for (const cat of inactiveList) {
                await api.put(`${API_BASE_URL}/api/categories/activate/${cat._id}`);
            }

            setCategories((prev) => [...prev, ...inactiveList]);
            setInactiveList([]);
            showNotification('All categories activated successfully', 'success');
        } catch (err) {
            showNotification('Error restoring all categories', 'error');
        }
    };

    const getExportRows = (rows = filteredActive) =>
        rows.map((category, index) => ({
            'S.No': index + 1,
            'Category Name': category.categoryName || '',
            'Has Rider': category.hasRider ? 'Yes' : 'No',
            'Has Scheme': category.hasScheme ? 'Yes' : 'No'
        }));

    const handleEmptyExport = () => {
        if (!filteredActive.length) {
            showNotification('No category records available to download', 'error');
            return true;
        }
        return false;
    };

    const exportCategoriesExcel = () => {
        if (handleEmptyExport()) return;
        const worksheet = XLSX.utils.json_to_sheet(getExportRows());
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Category Master');
        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        saveAs(new Blob([excelBuffer], { type: 'application/octet-stream' }), 'category-master.xlsx');
        setShowExportMenu(false);
    };

    const exportCategoriesCsv = () => {
        if (handleEmptyExport()) return;
        const worksheet = XLSX.utils.json_to_sheet(getExportRows());
        const csv = XLSX.utils.sheet_to_csv(worksheet);
        saveAs(new Blob([csv], { type: 'text/csv;charset=utf-8;' }), 'category-master.csv');
        setShowExportMenu(false);
    };

    const escapeHtml = (value) =>
        String(value ?? '')
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#039;');

    const exportCategoriesPdf = () => {
        if (handleEmptyExport()) return;
        const rows = getExportRows();
        const printWindow = window.open('', '_blank', 'noopener,noreferrer');
        if (!printWindow) {
            showNotification('Please allow popups to export PDF.', 'error');
            return;
        }

        const tableRows = rows.map((row) => `
            <tr>
                <td>${escapeHtml(row['S.No'])}</td>
                <td>${escapeHtml(row['Category Name'])}</td>
                <td>${escapeHtml(row['Has Rider'])}</td>
                <td>${escapeHtml(row['Has Scheme'])}</td>
            </tr>
        `).join('');

        printWindow.document.write(`
            <!doctype html>
            <html>
              <head>
                <title>Category Master Export</title>
                <style>
                  body { font-family: Arial, sans-serif; color: #111827; margin: 24px; }
                  h1 { margin: 0; font-size: 22px; }
                  p { margin: 6px 0 18px; color: #667085; font-size: 12px; }
                  table { width: 100%; border-collapse: collapse; font-size: 11px; }
                  th, td { border: 1px solid #d7e0ea; padding: 8px; text-align: left; vertical-align: top; }
                  th { background: #f1f5f9; text-transform: uppercase; font-size: 10px; }
                  tr:nth-child(even) td { background: #fbfdff; }
                  @media print { body { margin: 12mm; } }
                </style>
              </head>
              <body>
                <h1>Category Master</h1>
                <p>${rows.length} category record(s) exported on ${new Date().toLocaleString('en-IN')}</p>
                <table>
                  <thead>
                    <tr>
                      <th>S.No</th>
                      <th>Category Name</th>
                      <th>Has Rider</th>
                      <th>Has Scheme</th>
                    </tr>
                  </thead>
                  <tbody>${tableRows || '<tr><td colspan="4">No category records found.</td></tr>'}</tbody>
                </table>
                <script>
                  window.onload = function () {
                    window.print();
                  };
                </script>
              </body>
            </html>
        `);
        printWindow.document.close();
        setShowExportMenu(false);
    };

    if (checkingAuth) {
        return <div className="category-loading-screen">Loading...</div>;
    }

    if (!authenticated) {
        return <Navigate to="/" replace />;
    }

    return (
        <div className="category-master-container">
            <div className="sidebar">
                <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />
            </div>

            <div className={`category-content-shell ${collapsed ? 'collapsed' : ''}`}>
                <div className="category-sticky-header">
                    <Header />
                </div>

                <main className="category-scroll-area">
                    <section className="category-content">
                        <div className="category-page-hero">
                            <div>
                                <span className="category-page-eyebrow">System Settings / Product Master</span>
                                <h1>Category Master</h1>
                                <p>Manage product categories, rider availability, and scheme configuration used across policy setup.</p>
                            </div>
                            <div className="category-hero-stats">
                                <span>Active Categories</span>
                                <strong>{categories.length}</strong>
                                <small>{filteredActive.length} matching current filters</small>
                            </div>
                        </div>

                        <div className="category-summary-grid">
                            {summaryCards.map(({ label, value, note, icon: Icon, tone }) => (
                                <article className={`category-summary-card ${tone}`} key={label}>
                                    <Icon size={22} />
                                    <span>{label}</span>
                                    <strong>{value}</strong>
                                    <small>{note}</small>
                                </article>
                            ))}
                        </div>

                        <section className="category-filter-card">
                            <div className="category-filter-header">
                                <div>
                                    <h2>Search & Filters</h2>
                                    <p>Find categories by name and quickly filter rider or scheme enabled records.</p>
                                </div>
                                <span className="category-result-pill">{filteredActive.length} results</span>
                            </div>

                            <div className="category-filter-grid">
                                <label className="category-search-field">
                                    <span>Search Categories</span>
                                    <div>
                                        <Search size={17} />
                                        <input
                                            type="text"
                                            placeholder="Category name"
                                            value={activeSearchText}
                                            onChange={(e) => setActiveSearchText(e.target.value)}
                                        />
                                    </div>
                                </label>

                                <label>
                                    <span>Rider</span>
                                    <select value={activeRiderFilter} onChange={(e) => setActiveRiderFilter(e.target.value)}>
                                        <option value="">All</option>
                                        <option value="true">Has Rider</option>
                                        <option value="false">No Rider</option>
                                    </select>
                                </label>

                                <label>
                                    <span>Scheme</span>
                                    <select value={activeSchemeFilter} onChange={(e) => setActiveSchemeFilter(e.target.value)}>
                                        <option value="">All</option>
                                        <option value="true">Has Scheme</option>
                                        <option value="false">No Scheme</option>
                                    </select>
                                </label>
                            </div>

                            <div className="category-action-row">
                                {hasPrivilege(categoryPrivileges.add) && <button className="category-btn category-btn-primary" onClick={openAddPopup}>
                                    <Plus size={16} /> Add Category
                                </button>}

                                {hasPrivilege(categoryPrivileges.viewInactive) && <button className="category-btn category-btn-ghost" onClick={openInactivePopup}>
                                    <ArchiveRestore size={16} /> Inactive List
                                </button>}

                                <button
                                    className="category-btn category-btn-ghost"
                                    onClick={() => {
                                        setActiveSearchText('');
                                        setActiveRiderFilter('');
                                        setActiveSchemeFilter('');
                                    }}
                                >
                                    <RefreshCw size={16} /> Reset
                                </button>

                                {hasPrivilege(categoryPrivileges.download) && <div className="category-export-menu">
                                    <button
                                        className="category-btn category-btn-export"
                                        onClick={() => setShowExportMenu((prev) => !prev)}
                                    >
                                        <Download size={16} /> Download <ChevronDown size={15} />
                                    </button>
                                    {showExportMenu && (
                                        <div className="category-export-dropdown">
                                            <button onClick={exportCategoriesCsv}>CSV</button>
                                            <button onClick={exportCategoriesExcel}>Excel</button>
                                            <button onClick={exportCategoriesPdf}>PDF</button>
                                        </div>
                                    )}
                                </div>}
                            </div>
                        </section>

                        <section className="category-table-card">
                            <div className="category-table-toolbar">
                                <div>
                                    <h2>Category Records</h2>
                                    <p>Review category configuration and update rider or scheme flags.</p>
                                </div>
                                <span className="category-result-pill">{activeCategories.length} shown</span>
                            </div>

                            <div className="category-table-scroll">
                                {loading ? (
                                    <div className="category-loading-panel">Loading categories...</div>
                                ) : (
                                    <table className="category-table">
                                        <thead>
                                            <tr>
                                                <th className="category-sticky-name">Category</th>
                                                <th>Has Rider</th>
                                                <th>Has Scheme</th>
                                                <th>Configuration</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {activeCategories.length ? (
                                                activeCategories.map((category) => (
                                                    <tr key={category._id}>
                                                        <td className="category-sticky-name" data-label="Category">
                                                            <div className="category-name-cell">
                                                                <span className="category-avatar">
                                                                    <FolderTree size={17} />
                                                                </span>
                                                                <span>
                                                                    <strong>{category.categoryName}</strong>
                                                                    <small>Product category</small>
                                                                </span>
                                                            </div>
                                                        </td>
                                                        <td data-label="Has Rider">
                                                            <span className={`category-status ${category.hasRider ? 'is-active' : ''}`}>
                                                                {category.hasRider ? 'Yes' : 'No'}
                                                            </span>
                                                        </td>
                                                        <td data-label="Has Scheme">
                                                            <span className={`category-status ${category.hasScheme ? 'is-active' : ''}`}>
                                                                {category.hasScheme ? 'Yes' : 'No'}
                                                            </span>
                                                        </td>
                                                        <td data-label="Configuration">
                                                            <span className="category-chip">
                                                                {category.hasRider && category.hasScheme
                                                                    ? 'Rider + Scheme'
                                                                    : category.hasRider
                                                                        ? 'Rider Enabled'
                                                                        : category.hasScheme
                                                                            ? 'Scheme Enabled'
                                                                            : 'Standard'}
                                                            </span>
                                                        </td>
                                                        <td data-label="Actions">
                                                            <div className="category-action-icons">
                                                                {hasPrivilege(categoryPrivileges.edit) && <button className="category-icon-btn edit-btn" onClick={() => handleEditCategory(category)} title="Edit Category" aria-label={`Edit ${category.categoryName}`}>
                                                                    <Edit size={18} />
                                                                </button>}
                                                                {hasPrivilege(categoryPrivileges.deactivate) && <button
                                                                    className="category-icon-btn delete-btn"
                                                                    onClick={() => {
                                                                        setCategoryToDelete(category._id);
                                                                        setShowDeleteConfirm(true);
                                                                    }}
                                                                    title="Deactivate Category"
                                                                    aria-label={`Deactivate ${category.categoryName}`}
                                                                >
                                                                    <Ban size={18} />
                                                                </button>}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="5">
                                                        <div className="category-empty-state">
                                                            <Tags size={42} />
                                                            <h3>No Categories Found</h3>
                                                            <p>Try changing the filters or add a new category.</p>
                                                            {hasPrivilege(categoryPrivileges.add) && <button className="category-btn category-btn-primary" onClick={openAddPopup}>
                                                                <Plus size={16} /> Add Category
                                                            </button>}
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                        </section>

                        <div className="category-pagination-row">
                            <div className="category-pagination-meta">
                                <span>
                                    Showing {filteredActive.length ? activeFirst + 1 : 0}-{Math.min(activeLast, filteredActive.length)} of {filteredActive.length}
                                </span>
                                <small>Filtered category records</small>
                            </div>

                            <div className="category-pagination-controls">
                                <Pagination
                                    totalItems={filteredActive.length}
                                    itemsPerPage={activeItemsPerPage}
                                    currentPage={activePage}
                                    onPageChange={setActivePage}
                                />
                            </div>

                            <div className="category-items-per-page">
                                <label>Items per page:</label>
                                <select
                                    value={activeItemsPerPage}
                                    onChange={(e) => {
                                        setActiveItemsPerPage(Number(e.target.value));
                                        setActivePage(1);
                                    }}
                                >
                                    <option value={5}>5</option>
                                    <option value={10}>10</option>
                                    <option value={20}>20</option>
                                    <option value={50}>50</option>
                                    <option value={100}>100</option>
                                </select>
                            </div>
                        </div>

                        {showDeleteConfirm && (
                            <div className="category-confirmation-modal-overlay">
                                <div className="category-confirmation-modal-content">
                                    <h3>Confirm Deactivation</h3>
                                    <p>Are you sure you want to deactivate this category?</p>

                                    <div className="category-confirmation-modal-actions">
                                        <button className="category-confirm-btn" onClick={confirmDelete}>Yes</button>
                                        <button
                                            className="category-cancel-btn"
                                            onClick={() => {
                                                setShowDeleteConfirm(false);
                                                setCategoryToDelete(null);
                                            }}
                                        >
                                            No
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {showPopup && (
                            <div className="category-popup-overlay">
                                <div className="category-popup">
                                    <button className="category-popup-close" onClick={resetForm} aria-label="Close category form">
                                        <X size={18} />
                                    </button>
                                    <h2>{editMode ? 'Edit Category' : 'Add Category'}</h2>
                                    <p className="category-popup-subtitle">Define category identity and availability for riders or schemes.</p>

                                    <form onSubmit={handleFormSubmit} className="category-form">
                                        <label className="category-form-full">
                                            <span>Category Name</span>
                                            <input
                                                type="text"
                                                placeholder="Enter category name"
                                                value={formData.categoryName}
                                                onChange={(e) => setFormData((prev) => ({ ...prev, categoryName: e.target.value }))}
                                                maxLength={50}
                                                required
                                            />
                                        </label>

                                        <div className="category-toggle-card">
                                            <div>
                                                <strong>Has Rider</strong>
                                                <small>Enable rider configuration for this category.</small>
                                            </div>
                                            <label className="category-switch">
                                                <input
                                                    type="checkbox"
                                                    checked={formData.hasRider}
                                                    onChange={() => setFormData((prev) => ({ ...prev, hasRider: !prev.hasRider }))}
                                                />
                                                <span></span>
                                            </label>
                                        </div>

                                        <div className="category-toggle-card">
                                            <div>
                                                <strong>Has Scheme</strong>
                                                <small>Enable scheme configuration for this category.</small>
                                            </div>
                                            <label className="category-switch">
                                                <input
                                                    type="checkbox"
                                                    checked={formData.hasScheme}
                                                    onChange={() => setFormData((prev) => ({ ...prev, hasScheme: !prev.hasScheme }))}
                                                />
                                                <span></span>
                                            </label>
                                        </div>

                                        <div className="category-popup-actions">
                                            <button type="submit" className="category-submit-btn">
                                                {editMode ? 'Update Category' : 'Save Category'}
                                            </button>
                                            <button type="button" className="category-popup-cancel-btn" onClick={resetForm}>
                                                Cancel
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        )}

                        {showInactivePopup && (
                            <div className="category-popup-overlay">
                                <div className="category-popup large">
                                    <button className="category-popup-close" onClick={() => setShowInactivePopup(false)} aria-label="Close inactive categories">
                                        <X size={18} />
                                    </button>
                                    <h2>Inactive Categories</h2>
                                    <p className="category-popup-subtitle">Review deactivated categories and restore records when required.</p>

                                    <div className="category-inactive-filters">
                                        <input
                                            type="text"
                                            placeholder="Search inactive categories"
                                            value={inactiveSearchText}
                                            onChange={(e) => setInactiveSearchText(e.target.value)}
                                        />

                                        <select value={sortField} onChange={(e) => setSortField(e.target.value)}>
                                            <option value="">Sort By</option>
                                            <option value="categoryName">Category Name</option>
                                            <option value="hasRider">Has Rider</option>
                                            <option value="hasScheme">Has Scheme</option>
                                        </select>

                                        <select value={sortOrder} onChange={(e) => setSortOrder(e.target.value)}>
                                            <option value="asc">ASC</option>
                                            <option value="desc">DESC</option>
                                        </select>

                                        <button className="category-restore-all-btn" onClick={restoreAll}>
                                            <ArchiveRestore size={15} /> Restore All
                                        </button>
                                    </div>

                                    <div className="category-inactive-table-scroll">
                                        <table className="category-inactive-table">
                                            <thead>
                                                <tr>
                                                    <th>Category</th>
                                                    <th>Has Rider</th>
                                                    <th>Has Scheme</th>
                                                    <th>Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {inactiveCategories.length ? (
                                                    inactiveCategories.map((item) => (
                                                        <tr key={item._id}>
                                                            <td>{item.categoryName}</td>
                                                            <td>{item.hasRider ? 'Yes' : 'No'}</td>
                                                            <td>{item.hasScheme ? 'Yes' : 'No'}</td>
                                                            <td>
                                                                <button className="category-activate-btn" onClick={() => activateCategory(item._id)}>
                                                                    Restore
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan="4">No inactive categories found.</td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>

                                    <div className="category-modal-pagination-row">
                                        <Pagination
                                            totalItems={filteredInactive.length}
                                            itemsPerPage={inactiveItemsPerPage}
                                            currentPage={inactivePage}
                                            onPageChange={setInactivePage}
                                        />

                                        <div className="category-items-per-page">
                                            <label>Items per page:</label>
                                            <select
                                                value={inactiveItemsPerPage}
                                                onChange={(e) => {
                                                    setInactiveItemsPerPage(Number(e.target.value));
                                                    setInactivePage(1);
                                                }}
                                            >
                                                <option value={5}>5</option>
                                                <option value={10}>10</option>
                                                <option value={20}>20</option>
                                                <option value={50}>50</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </section>
                </main>
            </div>
        </div>
    );
};

export default CategoryMaster;
